import React from 'react';
import theme from '../../../theme';
import { DateInput, DateValue, Input, Select, Selection, SelectItem, Switch, Table, TableBody, TableCell, TableColumn, TableHeader, TableRow, Textarea } from '@nextui-org/react';
import { FamilyFormData, FamilyPersonality, FamilyTraitData } from '../../../../types/families';
import { CalendarDate, Trait, UsefulInfo } from '../../../../types/common';
import { DEFAULT_COMMON_USEFUL_INFO, TRAITS_SELECTOR_DATA } from '../../../../constants/common';
import { CAREGIVER_COVERAGE_AVAILABILITY, CAREGIVER_NATIONALITIES_SELECTOR_DATA } from '../../../../constants/caregivers';
import { FAMILY_NIGHT_SLEEP_SLECTOR_DATA, FAMILY_OTHER_PEOPLE_PRESENCE_SLECTOR_DATA, FAMILY_PERSONALITIES, FAMILY_PERSONALITIES_SELECTOR_DATA, FAMILY_SMOKE_ACCEPTANCE_SLECTOR_DATA, FAMILY_WEEKEND_NEEDS_SLECTOR_DATA } from '../../../../constants/family';
import { brkRef, calendarDate2Timestamp } from '../../../../helpers/utils';
import Typography from '../../common/Typography';
import { CareGiverCovergaeAvailability, CaregiverNationality } from '../../../../types/caregivers';
import Button from '../../common/Button';
import { parseDate } from '@internationalized/date';
import CustomModal from '../../common/CustomModal';
import IconButton from '../../common/IconButton';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';

const NATIONALITIES_TEXT = "Ogni nazionalità ha le sue caratteristiche, per nostra esperienza non ce n'è realmente una migliore dell'altra. Noi non facciamo differenze e diamo priorità alla valutazione del loro potenziale lavorativo: sia per soft skill, sia per esperienza pregressa. Nel caso abbiate preferenze in merito, ci sono delle nazionalità che volete escludere dalla ricerca ?";

interface IFamilyForm {
  initialData?: FamilyFormData;
  loading?: boolean;
  onSubmit: (familyFormData: FamilyFormData) => void;
  onCancel: () => void;
  interviewMode?: boolean;
  className?: string;
  style?: React.CSSProperties;
}

export default function FamilyForm({
  initialData,
  onCancel,
  onSubmit,
  loading = false,
  interviewMode = false,
  className,
  style
}: IFamilyForm) {

  /* ANAGRAFICA */
  const [name, setName] = React.useState<string>('');
  const [address, setAddress] = React.useState<string>('');
  const [surname, setSurname] = React.useState<string>('');
  const [weight, setWeight] = React.useState<string>('');
  const [height, setHeight] = React.useState<string>('');
  const [birthDate, setBirthDate] = React.useState<DateValue | undefined>();
  const [telephone, setTelephone] = React.useState<string>('');
  const [coverage, setCoverage] = React.useState<Selection>(new Set([]));
  /* NAZIONALITA ESCLUSE */
  const [excludedNationalities, setExcludedNationalities] = React.useState<Selection>(new Set([]));
  /* CARATTERISTICHE */
  const [traits, setTraits] = React.useState<FamilyTraitData[]>([]);
  /* CARATTERE PAZIENTE */
  const [personalities, setPersonalities] = React.useState<FamilyPersonality[]>([]);
  /* INFO UTILI */
  const [weekendNeeds, setWeekendNeeds] = React.useState<Selection>(new Set([]));
  const [nightSleep, setNightSleep] = React.useState<Selection>(new Set([]));
  const [otherPeoplePresence, setOtherPeoplePresence] = React.useState<Selection>(new Set([]));
  const [smokeAcceptance, setSmokeAcceptance] = React.useState<Selection>(new Set([]));
  const [caregiverRoomPresence, setCaregiverRoomPresence] = React.useState<boolean>(false);
  const [surveilanceCamera, setSurveillanceCamera] = React.useState<boolean>(false);
  // notes
  const [traitNoteValue, setTraitNoteValue] = React.useState<string>('');
  const [selectedTrait, setSelectedTrait] = React.useState<Trait | null>(null);
  // family notes
  const [notes, setNotes] = React.useState<string>('');

  React.useEffect(() => {
    if (initialData) {
      init(initialData);
    }
  }, []);

  const init = (iData: FamilyFormData) => {
    setName(iData.personalData.name);
    setAddress(iData.personalData.address);
    setSurname(iData.personalData.surname);
    setWeight(iData.personalData.weight.toString());
    setHeight(iData.personalData.height.toString());
    const date = new Date(iData.personalData.tsDateOfBirth);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const stringInitialBirthDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const initialBirthDate = parseDate(stringInitialBirthDate);
    setBirthDate(initialBirthDate);
    setTelephone(iData.personalData.telephone);
    setCoverage(iData.personalData.coverage ? new Set([iData.personalData.coverage]) : new Set([]));
    setExcludedNationalities(new Set(iData.excludedNationalities));
    setTraits(iData.traits);
    setPersonalities(iData.personalities);
    // TODO: Refactor
    const weekendNeedsValue = iData.usefulInfo.find(el => el.name === 'Bisogno nei weekend')?.value
    setWeekendNeeds(weekendNeedsValue ? new Set([weekendNeedsValue]): new Set([]));
    const nightSleepValue = iData.usefulInfo.find(el => el.name === 'Di notte dorme')?.value
    setNightSleep(nightSleepValue ? new Set([nightSleepValue]): new Set([]));
    const smokeAcceptanceValue = iData.usefulInfo.find(el => el.name === 'Accettereste una persona fumatrice')?.value
    setSmokeAcceptance(smokeAcceptanceValue ? new Set([smokeAcceptanceValue]): new Set([]));
    const otherPeoplePresenceValue = iData.usefulInfo.find(el => el.name === 'Presenza di altri familiari in casa')?.value
    setOtherPeoplePresence(otherPeoplePresenceValue ? new Set([otherPeoplePresenceValue]): new Set([]));
    const surveilanceCameraValue = iData.usefulInfo.find(el => el.name === 'Presenza di telecamere di sorveglianza')?.value
    setSurveillanceCamera(surveilanceCameraValue === 'Sì');
    const caregiverRoomPresenceValue = iData.usefulInfo.find(el => el.name === "C'è la stanza per la badante")?.value;
    setCaregiverRoomPresence(caregiverRoomPresenceValue === 'Sì');
    setNotes(iData.note ?? '');
  }

  const getFamilyFormData = () => {
    const calendarDate: CalendarDate = {
      day: birthDate?.day as number,
      month: birthDate?.month as number,
      year: birthDate?.year as number,
    };
    const useful_info = getUsefulInfoObj();
    onSubmit({
      traits: traits,
      excludedNationalities: Array.from(excludedNationalities) as CaregiverNationality[],
      personalities: Array.from(personalities) as FamilyPersonality[],
      usefulInfo: useful_info,
      note:notes,
      personalData: {
        address: address,
        name: name,
        height: parseInt(height),
        telephone: telephone,
        surname: surname,
        tsDateOfBirth: calendarDate2Timestamp(calendarDate),
        weight: parseInt(weight),
        coverage: Array.from(coverage).length > 0 ? Array.from(coverage)[0] as CareGiverCovergaeAvailability : undefined
      }
    })
  }

  const getUsefulInfoObj = (): UsefulInfo[] => {
    return [
      {name: "Di notte dorme", value: Array.from(nightSleep).length > 0 ? Array.from(nightSleep)[0] as string : FAMILY_NIGHT_SLEEP_SLECTOR_DATA[0].value },
      {name: "Bisogno nei weekend", value: Array.from(weekendNeeds).length > 0 ? Array.from(weekendNeeds)[0] as string : FAMILY_WEEKEND_NEEDS_SLECTOR_DATA[0].value },
      {name: "Accettereste una persona fumatrice", value: Array.from(smokeAcceptance).length > 0 ? Array.from(smokeAcceptance)[0] as string : FAMILY_SMOKE_ACCEPTANCE_SLECTOR_DATA[0].value },
      {name: "Presenza di altri familiari in casa", value: Array.from(otherPeoplePresence).length > 0 ? Array.from(otherPeoplePresence)[0] as string : FAMILY_OTHER_PEOPLE_PRESENCE_SLECTOR_DATA[0].value },
      {name: "Presenza di telecamere di sorveglianza", value: surveilanceCamera ? "Sì" : "No"},
      {name: "C'è la stanza per la badante", value: caregiverRoomPresence ? "Sì" : "No"}
    ]
  }

  // traits
  const onAddTrait = (
    e: any
  ) => {
    const val = Array.from(new Set(e))[0] as Trait;
    const idx = traits.findIndex(el => el.trait === val);
    if (idx < 0) {
      setTraits(prev => {
        const newTraits = brkRef(prev);
        newTraits.push({trait: val, note: ''});
        return newTraits;
      })
    }
  }

  const onDeleteTrait = (
    t: FamilyTraitData
  ) => {
    setTraits(prev => {
      const newTrais = brkRef(prev);
      const idx = prev.findIndex(el => el.trait === t.trait);
      if (idx >= 0) {
        newTrais.splice(idx, 1);
      }
      return newTrais;
    }) 
  }

  const onTraitNoteEdit = () => {
    if (!selectedTrait) return;
    setTraits(prev => {
      const newTraits = brkRef(prev);
      const idx = newTraits.findIndex(el => el.trait === selectedTrait);
      if (idx >= 0) {
        newTraits[idx].note = traitNoteValue;
      }
      return newTraits;
    })
  }

  const onDeletePersonality = (
    p: FamilyPersonality
  ) => {
    setPersonalities(prev => {
      const newPers = brkRef(prev);
      const idx = newPers.findIndex(el => el === p);
      if (idx >= 0) {
        newPers.splice(idx, 1);
      }
      return newPers;
    })
  }

  const onAddPersonality = (e: any) => {
    const p = Array.from(new Set(e))[0] as FamilyPersonality;
    setPersonalities(prev => {
      return [...prev, p];
    })
  }

  return (
    <div className={`flex flex-col w-full h-full ${className ?? ""}`} style={style}>
      <div className="flex flex-col mt-8 gap-4">
        <Typography style={{ fontWeight: 700 }} colorVariant="textTertiary">
          ANAGRAFICA
        </Typography>
        <div className="flex gap-4">
          <Input value={name} onValueChange={setName} label="Nome" className="flex-[4]" />
          <Input value={surname} onValueChange={setSurname} label="Cognome" className="flex-[4]" />
          <DateInput label={"Data di nascita"} className="flex-[2]" value={birthDate} onChange={setBirthDate} isInvalid={false} />
        </div>
        <div className="flex gap-4">
          <Input value={address} onValueChange={setAddress} label="Indirizzo" className="flex-[5]" />
          <Input value={telephone} onValueChange={setTelephone} label="Telefono" className="flex-[5]" />
        </div>
        <div className="flex gap-4">
          <Input value={weight} onValueChange={setWeight} label="Peso" className="flex-[5]" />
          <Input value={height} onValueChange={setHeight} label="Altezza" className="flex-[5]" />
        </div>
        <Select label="Che tipo di copertura è richiesta?" selectedKeys={coverage} onSelectionChange={setCoverage}>
          {CAREGIVER_COVERAGE_AVAILABILITY.map((e) => (
            <SelectItem key={e.key}>{e.value}</SelectItem>
          ))}
        </Select>
      </div>

      <div className="mt-8">
        <Typography style={{ fontWeight: 700 }} colorVariant="textTertiary">
          NAZIONALITA' ESCLUSE
        </Typography>
        <Typography variant="label" colorVariant="textLabel" className="my-3">
          {NATIONALITIES_TEXT}
        </Typography>
        <Select
          className="mt-4"
          label="Seleziona delle nazionalità"
          selectionMode="multiple"
          selectedKeys={excludedNationalities}
          onSelectionChange={setExcludedNationalities}
        >
          {CAREGIVER_NATIONALITIES_SELECTOR_DATA.map((region) => (
            <SelectItem key={region.key}>{region.value}</SelectItem>
          ))}
        </Select>
      </div>

      <div className="mt-8">
        <Typography style={{ fontWeight: 700 }} colorVariant="textTertiary">
          CARATTERISTICHE ASSISTENZA
        </Typography>
        {traits.length > 0 && (
          <Table removeWrapper className="mt-3">
            <TableHeader>
              <TableColumn>NOME CARATTERISTICA</TableColumn>
              <TableColumn>NOTE</TableColumn>
            </TableHeader>
            <TableBody>
              {traits.map((ft, i) => (
                <TableRow key={i}>
                  <TableCell className="flex items-center">
                    <IconButton
                      backgroundColorVariant="none"
                      onClick={() => onDeleteTrait(ft)}
                      icon={<DeleteRoundedIcon style={{ color: theme.color.primary }} />}
                    />
                    <Typography colorVariant="primary" variant="button" style={{ fontWeight: 600 }}>
                      {ft.trait}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: "flex", alignItems: "center" }}>
                      {ft.note ? (
                        <Typography
                          variant="button"
                          style={{
                            whiteSpace: "nowrap",
                            maxWidth: "300px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {ft.note}
                        </Typography>
                      ) : (
                        <Typography variant="button" colorVariant="textLabel" style={{ fontStyle: "italic" }}>
                          Nessuna nota
                        </Typography>
                      )}
                      <IconButton
                        backgroundColorVariant="none"
                        onClick={() => {
                          setTraitNoteValue(ft.note ?? "");
                          setSelectedTrait(ft.trait);
                        }}
                        icon={<EditRoundedIcon style={{ color: theme.color.primary }} />}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <Select className="mt-4" label="Seleziona delle caratteristiche" selectedKeys={new Set([])} onSelectionChange={onAddTrait}>
          {TRAITS_SELECTOR_DATA.map((trait) => (
            <SelectItem key={trait.key}>{trait.value}</SelectItem>
          ))}
        </Select>
      </div>

      <div className="mt-8">
        <Typography style={{ fontWeight: 700 }} colorVariant="textTertiary">
          CARATTERE PAZIENTE
        </Typography>
        {
          personalities.length > 0 && (
            <Table removeWrapper className="mt-3">
            <TableHeader>
              <TableColumn>PERSONALITA'</TableColumn>
            </TableHeader>
            <TableBody>
              {personalities.map((p, i) => (
                <TableRow key={i}>
                  <TableCell className="flex items-center">
                    <IconButton
                      backgroundColorVariant="none"
                      onClick={() => onDeletePersonality(p)}
                      icon={<DeleteRoundedIcon style={{ color: theme.color.primary }} />}
                    />
                    <Typography colorVariant="primary" variant="button" style={{ fontWeight: 600 }}>
                      {p}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          )
        }
        <Select className="mt-4" label="Seleziona una caratteristica" selectedKeys={new Set([])} onSelectionChange={onAddPersonality}>
          {FAMILY_PERSONALITIES_SELECTOR_DATA.map((p) => (
            <SelectItem key={p.key}>{p.value}</SelectItem>
          ))}
        </Select>
      </div>

      <div className="mt-8">
        <Typography style={{ fontWeight: 700 }} colorVariant="textTertiary">
          INFO UTILI PER BADANTE
        </Typography>
        <div className="flex flex-col gap-4 mt-4">
          <div className="flex flex-row gap-4">
            <div className="flex flex-1">
              <p>Presenza di telecamere di sorveglianza</p>
            </div>
            <Switch isSelected={surveilanceCamera} onValueChange={setSurveillanceCamera} />
          </div>
          <div className="flex flex-row gap-4">
            <div className="flex flex-1">
              <p>C'è la stanza per la badante</p>
            </div>
            <Switch isSelected={caregiverRoomPresence} onValueChange={setCaregiverRoomPresence} />
          </div>
          <Select className="mt-4" label="Di notte dorme" selectedKeys={nightSleep} onSelectionChange={setNightSleep}>
            {FAMILY_NIGHT_SLEEP_SLECTOR_DATA.map((ns) => (
              <SelectItem key={ns.key}>{ns.value}</SelectItem>
            ))}
          </Select>
          <Select className="mt-4" label="Bisogno nei weekend" selectedKeys={weekendNeeds} onSelectionChange={setWeekendNeeds}>
            {FAMILY_WEEKEND_NEEDS_SLECTOR_DATA.map((wn) => (
              <SelectItem key={wn.key}>{wn.value}</SelectItem>
            ))}
          </Select>
          <Select className="mt-4" label="Accettereste una persona fumatrice" selectedKeys={smokeAcceptance} onSelectionChange={setSmokeAcceptance}>
            {FAMILY_SMOKE_ACCEPTANCE_SLECTOR_DATA.map((sa) => (
              <SelectItem key={sa.key}>{sa.value}</SelectItem>
            ))}
          </Select>
          <Select className="mt-4" label="Presenza di altri familiari in casa" selectedKeys={otherPeoplePresence} onSelectionChange={setOtherPeoplePresence}>
            {FAMILY_OTHER_PEOPLE_PRESENCE_SLECTOR_DATA.map((opp) => (
              <SelectItem key={opp.key}>{opp.value}</SelectItem>
            ))}
          </Select>
        </div>
      </div>
      <div className='mt-8'>
        <Typography
          style={{fontWeight: 700}}
          colorVariant='textTertiary'>
          ALTRO
        </Typography>
        <div className='mt-2 flex flex-col gap-4'>
          <Textarea label='Note' placeholder='Inserisci note'
            value={notes}
            onValueChange={setNotes} />
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-16">
        <Button loading={loading} disabled={loading} title="Salva famiglia" onClick={getFamilyFormData} />
        <Button style={{ marginTop: interviewMode ? theme.sizing.x1 : 0 }} disabled={loading} title="Annulla" variant="secondary" onClick={onCancel} />
      </div>
      <CustomModal
        isOpen={!!selectedTrait}
        onOpenChange={() => setSelectedTrait(null)}
        content={
          <div
            style={{
              display: "flex",
              flex: 1,
              paddingTop: theme.sizing.x5,
              flexDirection: "column",
              gap: theme.sizing.x4,
            }}
          >
            <div style={{ display: "flex", gap: theme.sizing.x2, flexDirection: "column" }}>
              <Typography variant="headline3" style={{ fontWeight: 700 }} colorVariant="textTertiary">
                Modifica Nota
              </Typography>
              <Textarea label="Note" placeholder="Inserisci note" maxLength={100} value={traitNoteValue} onValueChange={setTraitNoteValue} />
            </div>
            <div style={{ display: "flex", gap: theme.sizing.x1, marginLeft: "auto" }}>
              <Button
                title="Chiudi"
                onClick={() => {
                  setTraitNoteValue("");
                  setSelectedTrait(null);
                }}
              />
              <Button
                title="Salva"
                onClick={() => {
                  onTraitNoteEdit();
                  setTraitNoteValue("");
                  setSelectedTrait(null);
                }}
              />
            </div>
          </div>
        }
      />
    </div>
  );
}